/**
 * 黑名单相关的公共方法
 */
/**
 * 比较两个IP黑名单数据是否有变化
 * @param {Object} original - 原始黑名单数据
 * @param {Object} updated - 更新后的黑名单数据
 * @returns {Object} 包含是否有变化及变化详情的对象
 */
export const compareIpBlacklist = (original, updated) => {
  if (!original || !updated) {
    return { hasChanged: true, changes: ['数据不完整，无法比较'] };
  }

  const changes = [];
  let hasChanged = false;

  // 比较IP范围
  if (original.blocked_ip_start !== updated.blocked_ip_start) {
    changes.push('起始IP发生变化');
    hasChanged = true;
  }

  if (original.blocked_ip_end !== updated.blocked_ip_end) {
    changes.push('结束IP发生变化');
    hasChanged = true;
  }

  // 比较屏蔽时长
  if (original.note !== updated.note) {
    changes.push('屏蔽时长发生变化');
    hasChanged = true;
  }

  return {
    hasChanged,
    changes,
  };
};

/**
 * 格式化IP地址显示
 * @param {String} ip - IP地址
 * @returns {String} 格式化后的IP地址
 */
export const formatIpAddress = ip => {
  if (!ip) return '';
  return ip;
};

/**
 * 验证IP地址格式是否正确
 * @param {String} ip - IP地址
 * @returns {Boolean} 是否是有效的IP地址
 */
export const isValidIpAddress = ip => {
  if (!ip) return false;

  // 简单的IP地址验证正则
  const ipRegex = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
  if (!ipRegex.test(ip)) return false;

  // 验证每个段是否在0-255范围内
  const parts = ip.split('.');
  return parts.every(part => {
    const num = parseInt(part, 10);
    return num >= 0 && num <= 255;
  });
};

/**
 * 将 IP 地址转换为整数
 * @param {String} ip - IP地址
 * @returns {Number} - 整数表示
 */
export const ipToLong = ip => {
  const parts = ip.split('.');
  return (
    ((parseInt(parts[0], 10) << 24) >>> 0) +
    ((parseInt(parts[1], 10) << 16) >>> 0) +
    ((parseInt(parts[2], 10) << 8) >>> 0) +
    parseInt(parts[3], 10)
  );
};

/**
 * 将整数转换为 IP 地址
 * @param {Number} long - 整数表示
 * @returns {String} - IP地址
 */
export const longToIp = long => {
  return [
    (long >>> 24) & 255,
    (long >>> 16) & 255,
    (long >>> 8) & 255,
    long & 255,
  ].join('.');
};

import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

/**
 * 获取当前项目设置的语言环境
 * 注意：此函数必须在 Vue 组件的 setup 函数中调用
 * @returns {string} - 当前项目设置的语言代码
 */
export function getCurrentLocale() {
  try {
    // 方法1：通过 Vue I18n 获取当前语言设置
    const { locale } = useI18n();
    if (locale && locale.value) {
      return locale.value;
    }

    // 方法2：通过 Vuex store 获取用户设置
    const store = useStore();
    const currentUser = store.getters['auth/getCurrentUser'];
    const uiSettings = currentUser.ui_settings || {};

    // 如果用户设置了语言，则使用用户设置的语言
    if (uiSettings.locale) {
      return uiSettings.locale;
    }

    // 默认使用浏览器语言
    return navigator.language || 'en';
  } catch (error) {
    // 如果出错，返回默认语言
    return 'en';
  }
}

/**
 * 获取当前项目设置的语言环境（非组件环境下使用）
 * 此函数不依赖 Vue 组件上下文，可以在任何地方调用
 * @param {object} store - Vuex store 实例
 * @returns {string} - 当前项目设置的语言代码
 */
export function getLocaleFromStore(store) {
  try {
    if (!store) {
      return navigator.language || 'en';
    }

    const currentUser = store.getters['auth/getCurrentUser'];
    const uiSettings = currentUser.ui_settings || {};

    // 如果用户设置了语言，则使用用户设置的语言
    if (uiSettings.locale) {
      return uiSettings.locale;
    }

    // 默认使用浏览器语言
    return navigator.language || 'en';
  } catch (error) {
    // 如果出错，返回默认语言
    return 'en';
  }
}

/**
 * 将本地时间转换为 ISO 8601 UTC 格式，用于 API 请求
 * @param {Date} localDate - 本地时区的 Date 对象
 * @returns {string} - ISO 8601 格式的 UTC 时间字符串
 */
export function convertLocalToUTC(localDate) {
  // 直接使用 toISOString() 将本地时间转换为 UTC 时间
  return localDate.toISOString();
}

/**
 * 根据语言环境格式化日期时间
 * @param {string|Date} date - 日期字符串或Date对象
 * @param {string} locale - 语言区域代码
 * @returns {string} - 格式化后的日期时间字符串
 */
export function formatDateByLocale(date, locale) {
  if (!date) return '---';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    // hour12: locale === 'en-US', // 可选，根据区域设置12/24小时制
  }).format(dateObj);
}
